import { Controller } from "@hotwired/stimulus"

/**
 * Column Preferences Controller
 *
 * Handles drag-and-drop reordering of columns and form submission
 * for the GP Index Column Manager feature.
 */
export default class extends Controller {
  static targets = ["gpForm", "lpForm", "gpColumnList", "lpColumnList", "columnItem", "dragHandle"]

  connect() {
    this.initDragAndDrop()
  }

  initDragAndDrop() {
    // Initialize drag and drop for GP column list
    if (this.hasGpColumnListTarget) {
      this.setupSortable(this.gpColumnListTarget)
    }

    // Initialize drag and drop for LP column list
    if (this.hasLpColumnListTarget) {
      this.setupSortable(this.lpColumnListTarget)
    }
  }

  setupSortable(list) {
    if (list.dataset.sortable !== "true") return

    const items = list.querySelectorAll('li')

    items.forEach(item => {
      const handle = item.querySelector('[data-column-preferences-target="dragHandle"]')
      if (!handle) return

      // Make item draggable via handle
      handle.addEventListener('mousedown', () => {
        item.setAttribute('draggable', 'true')
      })

      handle.addEventListener('mouseup', () => {
        item.setAttribute('draggable', 'false')
      })

      item.addEventListener('dragstart', this.handleDragStart.bind(this))
      item.addEventListener('dragover', this.handleDragOver.bind(this))
      item.addEventListener('dragenter', this.handleDragEnter.bind(this))
      item.addEventListener('dragleave', this.handleDragLeave.bind(this))
      item.addEventListener('drop', this.handleDrop.bind(this))
      item.addEventListener('dragend', this.handleDragEnd.bind(this))
    })
  }

  handleDragStart(event) {
    const item = event.target.closest('li')
    if (!item) return

    this.draggedItem = item
    item.classList.add('opacity-50', 'bg-primary-50', 'dark:bg-primary-900')

    // Required for Firefox
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/html', item.innerHTML)
  }

  handleDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    return false
  }

  handleDragEnter(event) {
    const item = event.target.closest('li')
    if (!item || item === this.draggedItem) return

    item.classList.add('border-t-2', 'border-primary-500')
  }

  handleDragLeave(event) {
    const item = event.target.closest('li')
    if (!item) return

    item.classList.remove('border-t-2', 'border-primary-500')
  }

  handleDrop(event) {
    event.stopPropagation()
    event.preventDefault()

    const targetItem = event.target.closest('li')
    if (!targetItem || !this.draggedItem || targetItem === this.draggedItem) return

    const list = targetItem.closest('ul')
    const items = Array.from(list.children)
    const draggedIndex = items.indexOf(this.draggedItem)
    const targetIndex = items.indexOf(targetItem)

    if (draggedIndex < targetIndex) {
      targetItem.after(this.draggedItem)
    } else {
      targetItem.before(this.draggedItem)
    }

    targetItem.classList.remove('border-t-2', 'border-primary-500')
    this.updateHiddenFields(list)

    return false
  }

  handleDragEnd(event) {
    const item = event.target.closest('li')
    if (!item) return

    item.setAttribute('draggable', 'false')
    item.classList.remove('opacity-50', 'bg-primary-50', 'dark:bg-primary-900')

    // Remove any lingering drag indicators
    document.querySelectorAll('.border-t-2.border-primary-500').forEach(el => {
      el.classList.remove('border-t-2', 'border-primary-500')
    })

    this.draggedItem = null
  }

  /**
   * Called when a column visibility checkbox is toggled
   */
  toggleColumn(event) {
    const checkbox = event.target
    const listItem = checkbox.closest('li')
    const list = listItem.closest('ul')

    if (checkbox.checked) {
      // Enable column in place (no reordering)
      listItem.classList.remove('bg-gray-50', 'dark:bg-gray-900')
      listItem.classList.add('bg-white', 'dark:bg-gray-800')

      // Add drag handle
      const handleContainer = listItem.querySelector('.col-span-1')
      if (handleContainer && !handleContainer.querySelector('[data-column-preferences-target="dragHandle"]')) {
        handleContainer.innerHTML = `
          <button type="button"
                  class="cursor-grab text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  data-column-preferences-target="dragHandle">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16"/>
            </svg>
          </button>
        `

        // Re-initialize drag and drop for this item
        this.setupSortable(list)
      }
    } else {
      // Disable column in place (no reordering)
      listItem.classList.remove('bg-white', 'dark:bg-gray-800')
      listItem.classList.add('bg-gray-50', 'dark:bg-gray-900')

      // Remove drag handle
      const handleContainer = listItem.querySelector('.col-span-1')
      if (handleContainer) {
        handleContainer.innerHTML = ''
      }
    }

    // Update form values without reordering
    this.updateHiddenFields(list)
  }

  /**
   * Sort list items so visible ones are at the top
   */
  sortListByVisibility(list) {
    const items = Array.from(list.children)
    const visibleItems = items.filter(item => {
      const checkbox = item.querySelector('input[type="checkbox"]')
      return checkbox && checkbox.checked
    })
    const hiddenItems = items.filter(item => {
      const checkbox = item.querySelector('input[type="checkbox"]')
      return !checkbox || !checkbox.checked
    })

    // Clear and re-append in order
    list.innerHTML = ''
    visibleItems.forEach(item => list.appendChild(item))
    hiddenItems.forEach(item => list.appendChild(item))
  }

  /**
   * Update hidden field values to maintain column order on form submission
   */
  updateHiddenFields(list) {
    // The checkboxes already serve as the form data
    // No additional hidden fields needed since visible_columns[] is an array
  }

  /**
   * Reset preferences to defaults for a given audience
   */
  resetToDefaults(event) {
    const audience = event.params.audience

    if (!confirm(`Are you sure you want to reset ${audience.toUpperCase()} column preferences to defaults? This cannot be undone.`)) {
      return
    }

    // Create a form to submit the reset action
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = window.location.pathname

    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    // Add method override for PATCH
    const methodInput = document.createElement('input')
    methodInput.type = 'hidden'
    methodInput.name = '_method'
    methodInput.value = 'PATCH'
    form.appendChild(methodInput)

    // Add page
    const pageInput = document.createElement('input')
    pageInput.type = 'hidden'
    pageInput.name = 'page'
    pageInput.value = new URLSearchParams(window.location.search).get('tab') || 'funds'
    form.appendChild(pageInput)

    // Add audience
    const audienceInput = document.createElement('input')
    audienceInput.type = 'hidden'
    audienceInput.name = 'audience'
    audienceInput.value = audience
    form.appendChild(audienceInput)

    // Add reset flag
    const resetInput = document.createElement('input')
    resetInput.type = 'hidden'
    resetInput.name = 'reset_to_defaults'
    resetInput.value = 'true'
    form.appendChild(resetInput)

    document.body.appendChild(form)
    form.submit()
  }
}
